import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { inked, toon } from "../toon";
import { buildEar, buildEye, eyeMaterials } from "./face-parts";
import { smoothstep } from "./geom";
import type { SkaterLook } from "./look";
import { skinMat } from "./materials";
import { buildCurtain, curtainFor, curtainPoint } from "./long-hair";
import { buildHairTie, buildTail, ponytailCurve, ponytailGeometry, ponytailRadius } from "./ponytail";

// The natural head (#75, the realistic rider's, `RiderBuild.face:
// "natural"`), head space, face +z: a one-piece skull, matte eyes, fine
// brows, a finer nose and lips, a mohawk of liberty spikes, and fine strand
// hair under headwear.

/**
 * The natural face: the toy's grin, catch-lights and round jaw swapped for
 * lips, matte eyes and a longer jaw on a one-piece head.
 */
export function buildNaturalFace(look: SkaterLook, into: THREE.Group, eyes: number): void {
  const skin = skinMat(look.skin);
  // One piece from crown to chin: no seam where a jaw meets the skull.
  const skull = inked(skullGeometry(), skin, 0.02);
  skull.name = "skull";
  into.add(skull);
  const mats = eyeMaterials(look);
  for (const side of [-1, 1]) {
    into.add(buildEye(side, eyes, mats));
    // A fine brow: thin, flat to the brow ridge, arched and tapering out.
    const geo = new THREE.CapsuleGeometry(0.0055, 0.05, 3, 8);
    geo.rotateZ(Math.PI / 2);
    const b = new THREE.Mesh(geo, mats.brow);
    b.name = "brow";
    b.scale.set(1, 0.8, 0.5);
    b.rotation.set(0, side * 0.45, side * 0.12);
    b.position.set(side * 0.074, 0.098, skullFrontZ(side * 0.074, 0.098) + 0.001);
    into.add(b);
    into.add(buildEar(side, skin));
  }
  // Finer and longer, with the bridge running up toward the brow.
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.03, 14, 10), skin);
  nose.scale.set(0.75, 1.2, 1.15);
  nose.position.set(0, -0.012, skullFrontZ(0, -0.012) - 0.005);
  into.add(nose);
  // Closed lips, a shade darker and redder than the skin unless the look says.
  const lipColor = new THREE.Color(look.lips ?? new THREE.Color(look.skin).lerp(new THREE.Color("#a8434a"), 0.45));
  const lipGeo = new THREE.CapsuleGeometry(0.0095, 0.034, 4, 10);
  lipGeo.rotateZ(Math.PI / 2);
  const lips = new THREE.Mesh(lipGeo, toon(lipColor, { roughness: 0.45 }, "skin"));
  lips.name = "lips";
  lips.scale.set(1, 1, 0.6);
  // On the skin, not sunk into the one-piece jaw or floating off it.
  lips.position.set(0, -0.074, skullFrontZ(0, -0.074) + 0.001);
  into.add(lips);
}

/**
 * The natural face's mohawk (#75, realistic): liberty spikes, each a
 * cluster of fine tapered strands gathered to a point, along a short strip
 * of hair on the shaved scalp. The strands merge into one mesh, so the whole
 * mohawk is two draws (the toy's cones take seven). Same arc and about the
 * same height as the toy's, so the head keeps its size.
 */
export function buildNaturalMohawk(color: string): THREE.Group {
  const mohawk = new THREE.Group();
  mohawk.name = "mohawk";
  const mat = toon(color, { roughness: 0.55 }, "hair");
  const R = 0.2;
  const SPIKES = 9;
  const PER = 5;
  const ROOT = 0.011;
  const rand = seeded(7);
  // A point on the skull's midline at arc angle a (0 is the crown, + toward the face),
  // `out` off the skin: the skull is 0.21 to the crown, 0.2 to the brow, a little fuller behind.
  const scalp = (a: number, out = 0) => {
    const z = (R + out) * Math.sin(a);
    return new THREE.Vector3(0, (CROWN + out) * Math.cos(a), z < 0 ? z * 1.03 : z);
  };
  const parts: THREE.BufferGeometry[] = [];
  const up = new THREE.Vector3(0, 1, 0);
  const q = new THREE.Quaternion();
  for (let i = 0; i < SPIKES; i++) {
    const u = i / (SPIKES - 1);
    const a = 0.95 - u * 2.05;
    // Rooted on the real crown (the toy's cones start inside it), so a little shorter.
    const h = 0.11 + 0.08 * Math.sin(Math.PI * Math.min(1, u * 1.15));
    // The spike's point: out from the skull, swept back a little.
    const tip = scalp(a - 0.25 * (h / 0.22), h);
    for (let k = 0; k < PER; k++) {
      // Roots spread across and along the strip; every strand runs to (nearly) the one point.
      const root = scalp(a + rand() * 0.09, -0.004);
      root.x = ((k - (PER - 1) / 2) / (PER - 1)) * 0.045 + rand() * 0.004;
      const end = tip.clone().add(new THREE.Vector3(rand(), rand(), rand()).multiplyScalar(0.008));
      const dir = end.clone().sub(root);
      const len = dir.length();
      const geo = new THREE.ConeGeometry(ROOT * (0.85 + 0.15 * Math.abs(rand())), len, 6, 1, true);
      geo.translate(0, len / 2, 0);
      geo.applyQuaternion(q.setFromUnitVectors(up, dir.normalize()));
      geo.translate(root.x, root.y, root.z);
      parts.push(geo.index ? geo.toNonIndexed() : geo);
    }
  }
  const merged = mergeGeometries(parts)!;
  for (const g of parts) g.dispose();
  merged.computeVertexNormals();
  const strands = new THREE.Mesh(merged, mat);
  strands.name = "strands";
  strands.userData = { strands: SPIKES * PER, root: ROOT };
  mohawk.add(strands);
  // The strip of hair the spikes grow from, forehead to nape: a tube half sunk in the scalp.
  const path = new THREE.CatmullRomCurve3(Array.from({ length: 9 }, (_, i) => scalp(1.05 - (i / 8) * 2.3, -0.01)));
  const strip = new THREE.Mesh(new THREE.TubeGeometry(path, 24, 0.024, 8), mat);
  strip.name = "hairline";
  mohawk.add(strip);
  return mohawk;
}

/** A fixed little generator in [-1, 1), so every build (and snapshot) is the same. */
function seeded(seed: number): () => number {
  return () => ((seed = (seed * 16807) % 2147483647) / 2147483647) * 2 - 1;
}

// The natural face's one-piece head (#75, realistic), head space, face +z.
// A lathed egg, the same crown (0.21) and cheek width (0.2) as the toy's
// sphere so the eyes, ears and headwear sit where they did, then bent out
// of round: the jaw narrows and tucks in at the back where the neck is, the
// chin comes forward, the mouth sits back under the nose, and cheekbones
// and a brow ridge swell out.
const CROWN = 0.21;
const CHIN = -0.24;

/** The skull's profile radius at height y, before it's bent. */
function skullRadius(y: number): number {
  if (y >= 0) return 0.2 * Math.sqrt(Math.max(0, 1 - (y / CROWN) ** 2));
  // A squarer fall-off below the cheeks: a jaw, then a rounded chin.
  return 0.2 * Math.max(0, 1 - (y / CHIN) ** 2.6) ** (1 / 2.6);
}

/** Bends one point of the round lathe into the head's shape, in place. */
function bendSkull(p: THREE.Vector3): void {
  const { x, y, z } = p;
  const low = smoothstep(-0.02, CHIN, y); // 0 at the cheeks, 1 at the chin
  // Narrower toward the chin.
  let nx = x * (1 - 0.26 * low);
  let nz = z;
  if (z < 0) {
    // The back of the jaw tucks in toward the neck; the back of the skull bulges a little.
    nz = z * (1 - 0.5 * low + 0.06 * smoothstep(-0.05, 0.08, y) * (1 - smoothstep(0.08, CROWN, y)));
  } else {
    // The mouth sits back under the nose; the chin comes forward again.
    nz = z * (1 - 0.1 * smoothstep(-0.03, -0.09, y) * (1 - smoothstep(-0.13, -0.2, y)));
    nz += 0.028 * smoothstep(-0.1, -0.21, y);
    // A brow ridge over the eyes.
    const across = Math.abs(x) / 0.2;
    nz += 0.007 * Math.exp(-(((y - 0.1) / 0.025) ** 2)) * Math.max(0, 1 - across * across);
  }
  // Cheekbones: out and forward, under and outside the eyes.
  const theta = Math.atan2(Math.abs(x), z);
  const cheek = 0.011 * Math.exp(-(((theta - 0.95) / 0.35) ** 2) - ((y + 0.03) / 0.035) ** 2);
  if (cheek > 1e-5) {
    const r = Math.hypot(nx, nz) || 1;
    nx += (nx / r) * cheek;
    nz += (nz / r) * cheek;
  }
  p.set(nx, y, nz);
}

let skullGeo: THREE.BufferGeometry | undefined;

/** The one-piece head, crown to chin: one lathe, one draw. */
export function skullGeometry(): THREE.BufferGeometry {
  if (skullGeo) return skullGeo.clone();
  const pts: THREE.Vector2[] = [];
  const STEPS = 28;
  for (let i = 0; i <= STEPS; i++) {
    // Denser near the chin and crown, where the profile turns fastest.
    const t = (1 - Math.cos((i / STEPS) * Math.PI)) / 2;
    const y = CHIN + (CROWN - CHIN) * t;
    pts.push(new THREE.Vector2(Math.max(skullRadius(y), i === 0 || i === STEPS ? 0 : 1e-4), y));
  }
  // Start the lathe at the back (phi = pi), so its seam runs down the back of the head.
  const SEGMENTS = 36;
  const geo = new THREE.LatheGeometry(pts, SEGMENTS, Math.PI);
  const pos = geo.getAttribute("position");
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    bendSkull(v.fromBufferAttribute(pos, i));
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  geo.computeVertexNormals();
  // The seam's two copies of each point share one normal, so it doesn't show.
  const normal = geo.getAttribute("normal");
  const n = new THREE.Vector3();
  const m = new THREE.Vector3();
  for (let j = 0; j < pts.length; j++) {
    const a = j;
    const b = SEGMENTS * pts.length + j;
    n.fromBufferAttribute(normal, a).add(m.fromBufferAttribute(normal, b)).normalize();
    normal.setXYZ(a, n.x, n.y, n.z);
    normal.setXYZ(b, n.x, n.y, n.z);
  }
  skullGeo = geo;
  return geo.clone();
}

/** How far forward the skull's skin is at (x, y) on the face side: where features sit. */
export function skullFrontZ(x: number, y: number): number {
  const r = skullRadius(y);
  // Undo the jaw's narrowing to find the round lathe's point, then bend it.
  const low = smoothstep(-0.02, CHIN, y);
  const rx = x / (1 - 0.26 * low);
  const p = new THREE.Vector3(rx, y, Math.sqrt(Math.max(0, r * r - rx * rx)));
  bendSkull(p);
  return p.z;
}

// The natural face's hair under headwear (#75, realistic): a thin scalp of
// hair that follows the one-piece skull down to a hairline, plus fine
// strands (tapered tubes along curves) merged into as few meshes as the toy
// shapes took.

/**
 * A point on the one-piece skull's skin at height y, `theta` round from the
 * face (+z, 0) toward +x, pushed `out` off the skin.
 */
function onSkull(theta: number, y: number, out = 0): THREE.Vector3 {
  const r = skullRadius(y);
  const p = new THREE.Vector3(r * Math.sin(theta), y, r * Math.cos(theta));
  bendSkull(p);
  const n = p.clone().normalize();
  return p.addScaledVector(n, out);
}

/** A hairline: its height all round the head, highest at the forehead (`front`) and dropping `drop` to the nape. */
function hairline(front: number, drop: number): (theta: number) => number {
  return (theta) => front - (drop / 2) * (1 - Math.cos(theta));
}

/** The scalp of hair: the skull's skin from the crown down to `line`, a little proud of it. Non-indexed, for merging. */
function hairCapGeometry(line: (theta: number) => number, out = 0.006): THREE.BufferGeometry {
  const AROUND = 36;
  const DOWN = 10;
  const pos: number[] = [];
  const uv: number[] = [];
  for (let i = 0; i <= AROUND; i++) {
    // From the back round, so the uv seam is at the nape.
    const theta = Math.PI + (i / AROUND) * Math.PI * 2;
    const last = Math.acos(Math.max(-1, Math.min(1, line(theta) / CROWN)));
    for (let j = 0; j <= DOWN; j++) {
      const phi = (j / DOWN) * last;
      const p = onSkull(theta, CROWN * Math.cos(phi), out);
      pos.push(p.x, p.y, p.z);
      uv.push(i / AROUND, j / DOWN);
    }
  }
  const index: number[] = [];
  for (let i = 0; i < AROUND; i++) {
    for (let j = 0; j < DOWN; j++) {
      const a = i * (DOWN + 1) + j;
      const b = a + DOWN + 1;
      index.push(a, a + 1, b, b, a + 1, b + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(index);
  geo.computeVertexNormals();
  return geo.toNonIndexed();
}

/**
 * One fine hair: a tube along a smooth curve through `pts`, from `radius`
 * at the root tapering to a tenth of it at the tip. Non-indexed, with
 * normals and uvs, so strands merge into one mesh.
 */
function strandGeometry(pts: THREE.Vector3[], radius: number, segs = 6, sides = 4): THREE.BufferGeometry {
  const curve = new THREE.CatmullRomCurve3(pts);
  const { normals, binormals } = curve.computeFrenetFrames(segs, false);
  const pos: number[] = [];
  const nor: number[] = [];
  const uv: number[] = [];
  const p = new THREE.Vector3();
  const n = new THREE.Vector3();
  for (let i = 0; i <= segs; i++) {
    curve.getPointAt(i / segs, p);
    const r = radius * (1 - 0.9 * (i / segs));
    for (let k = 0; k <= sides; k++) {
      const a = (k / sides) * Math.PI * 2;
      n.copy(normals[i]!).multiplyScalar(Math.cos(a)).addScaledVector(binormals[i]!, Math.sin(a));
      pos.push(p.x + n.x * r, p.y + n.y * r, p.z + n.z * r);
      nor.push(n.x, n.y, n.z);
      uv.push(k / sides, i / segs);
    }
  }
  const index: number[] = [];
  for (let i = 0; i < segs; i++) {
    for (let k = 0; k < sides; k++) {
      const a = i * (sides + 1) + k;
      const b = a + sides + 1;
      index.push(a, b, a + 1, b, b + 1, a + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(index);
  return geo.toNonIndexed();
}

/** Merges hair pieces into one named mesh, saying how many strands it holds and their root radius. */
function hairMesh(name: string, parts: THREE.BufferGeometry[], mat: THREE.Material, strands: number, root: number): THREE.Mesh {
  const merged = mergeGeometries(parts)!;
  for (const g of parts) g.dispose();
  const mesh = new THREE.Mesh(merged, mat);
  mesh.name = name;
  mesh.userData = { strands, root };
  return mesh;
}

const STRAND = 0.0045;

/**
 * The hair under the cowboy hat: a scalp of hair down to a hairline above
 * the ears, sideburns of fine strands in front of the ears, and short
 * strands falling from under the brim round the back. One mesh (the toy's
 * sideburns take two).
 */
export function buildHatHair(color: string): THREE.Mesh {
  const rand = seeded(11);
  const line = hairline(0.14, 0.12);
  const parts = [hairCapGeometry(line)];
  let count = 0;
  for (const side of [-1, 1]) {
    // Sideburns: down the cheek just in front of the ear, lying flat on the skin.
    for (let k = 0; k < 16; k++) {
      const t = side * (1.3 + 0.16 * (k / 15) + rand() * 0.01);
      const top = line(t) + 0.025 + rand() * 0.01;
      const end = 0.005 + rand() * 0.015;
      parts.push(
        strandGeometry([onSkull(t, top, 0.004), onSkull(t + side * 0.01, (top + end) / 2, 0.006), onSkull(t + side * 0.02, end, 0.004)], STRAND),
      );
      count++;
    }
  }
  // Round the back from ear to ear: shorter over the ears, longer at the nape.
  const BACK = 72;
  for (let k = 0; k < BACK; k++) {
    const t = 1.62 + (k / (BACK - 1)) * (Math.PI * 2 - 3.24) + rand() * 0.02;
    const overEar = Math.abs(Math.sin(t)) > 0.97;
    // Rooted well up under the scalp's edge, so the hair flows out of it.
    const top = line(t) + 0.03;
    const end = top - (overEar ? 0.045 : 0.08 + Math.abs(rand()) * 0.03);
    const sway = rand() * 0.04;
    parts.push(strandGeometry([onSkull(t, top, 0.004), onSkull(t + sway / 2, (top + end) / 2, 0.009), onSkull(t + sway, end, 0.006)], STRAND));
    count++;
  }
  return hairMesh("hair", parts, toon(color, { roughness: 0.6 }, "hair"), count, STRAND);
}

/** Where the space helmet's ponytail is tied, just out the back of the shell (head space). */
export const HELMET_TAIL_TIE: Readonly<THREE.Vector3> = new THREE.Vector3(0, -0.035, -0.275);

/**
 * The hair in the space helmet: a scalp of hair with side-swept bangs over
 * the forehead in the helmet's opening (one mesh, `fringe`), and a ponytail
 * of strands gathered in a tie at the back of the helmet, loosening toward
 * its tip (`ponytail`, `hair-tie`).
 */

export function buildHelmetHair(color: string): THREE.Mesh[] {
  const rand = seeded(23);
  const mat = toon(color, { roughness: 0.6 }, "hair");
  const line = hairline(0.135, 0.12);
  const bangs = [hairCapGeometry(line)];
  const BANGS = 30;
  for (let k = 0; k < BANGS; k++) {
    // Rooted high on the forehead under the visor, swept toward +x, ending above the brows.
    const t = -0.85 + (k / (BANGS - 1)) * 1.7 + rand() * 0.02;
    const sweep = 0.22 + rand() * 0.06;
    const end = 0.116 + Math.abs(rand()) * 0.012;
    bangs.push(strandGeometry([onSkull(t, 0.18, 0.004), onSkull(t + sweep / 2, 0.145, 0.011), onSkull(t + sweep, end, 0.006)], STRAND));
  }
  const fringe = hairMesh("fringe", bangs, mat, BANGS, STRAND);

  // The tail's line: out the back of the helmet, down and back (the toy tail's axis).
  const tie = HELMET_TAIL_TIE.clone();
  const down = new THREE.Vector3(0, -0.878, -0.479);
  const TAIL = 64;
  const tail: THREE.BufferGeometry[] = [];
  const jitter = (r: number) => new THREE.Vector3(rand() * r, rand() * r, rand() * r * 0.5);
  for (let k = 0; k < TAIL; k++) {
    const len = 0.2 + Math.abs(rand()) * 0.06;
    const root = new THREE.Vector3(rand() * 0.03, 0.02 + rand() * 0.02, -0.2);
    const bulge = tie.clone().addScaledVector(down, len * 0.45).add(jitter(0.034));
    const tip = tie.clone().addScaledVector(down, len).add(new THREE.Vector3(0, -0.03, 0)).add(jitter(0.024));
    tail.push(strandGeometry([root, tie.clone().add(jitter(0.01)), bulge, tip], STRAND * 1.1, 8));
  }
  const ponytail = hairMesh("ponytail", tail, mat, TAIL, STRAND * 1.1);

  const band = new THREE.Mesh(new THREE.TorusGeometry(0.016, 0.006, 6, 14), toon("#2a2a33", { roughness: 0.6 }, "cloth"));
  band.name = "hair-tie";
  band.position.copy(tie);
  band.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), down);
  return [fringe, ponytail, band];
}

/**
 * The natural face's long hair (#227): a scalp of hair down to a low
 * hairline, a fringe of strands swept from a parting across the forehead,
 * strands falling over the ears to the jaw (one mesh, `hair`), and the
 * fall down the back (`long-hair.ts`'s curtain) with strands lying along
 * it, which swing with it.
 */
export function buildNaturalLongHair(color: string, size: number): THREE.Group {
  const rand = seeded(41);
  const group = new THREE.Group();
  group.name = "long-hair";
  const mat = toon(color, { roughness: 0.6 }, "hair");
  const line = hairline(0.15, 0.2);
  const parts = [hairCapGeometry(line, 0.008)];
  // The fringe: rooted along a parting off to one side, swept across to the far temple, above the brows.
  const FRINGE = 36;
  for (let k = 0; k < FRINGE; k++) {
    const t = 0.35 - (k / (FRINGE - 1)) * 0.95 + rand() * 0.02;
    const sweep = -0.35 - Math.abs(rand()) * 0.1;
    const end = 0.13 + Math.abs(rand()) * 0.012;
    parts.push(strandGeometry([onSkull(0.35, 0.2, 0.004), onSkull(t + sweep / 3, 0.18, 0.012), onSkull(t + sweep, end, 0.008)], STRAND));
  }
  // Locks over each ear, from the temple down to the jaw.
  const LOCK = 24;
  for (const side of [-1, 1]) {
    for (let k = 0; k < LOCK; k++) {
      const t = side * (1.35 + (k / (LOCK - 1)) * 0.5 + rand() * 0.03);
      const end = -0.13 - Math.abs(rand()) * 0.05;
      parts.push(
        strandGeometry([onSkull(t * 0.85, 0.16, 0.006), onSkull(t, 0.06, 0.02), onSkull(t, -0.04, 0.03), onSkull(t * 1.03, end, 0.034)], STRAND, 8),
      );
    }
  }
  group.add(hairMesh("hair", parts, mat, FRINGE + 2 * LOCK, STRAND));
  // The fall: the curtain, with strands lying along its outer surface for texture.
  const shape = curtainFor(size, 0.222, 24, 16);
  const FALL = 48;
  const strands: THREE.BufferGeometry[] = [];
  for (let k = 0; k < FALL; k++) {
    const u = (k + 0.5) / FALL + rand() * 0.006;
    const end = 0.94 + Math.abs(rand()) * 0.06;
    const pts = [0.08, 0.35, 0.65, end].map((v) => curtainPoint(shape, u, v, -0.004));
    strands.push(strandGeometry(pts, STRAND * 1.2, 8));
  }
  const lay = hairMesh("hair-strands", strands, mat, FALL, STRAND * 1.2);
  group.add(buildCurtain(shape, mat, [lay]));
  return group;
}

/**
 * The natural face's ponytail (#234): a scalp of hair with a light fringe,
 * strands swept back over the skull from the hairline to a tie high on the
 * back of the head (one mesh, `hair`), and the tail hanging from the tie
 * (`ponytail.ts`'s line): a core with strands lying along it, which swing
 * together (`hair-swing`).
 */
export function buildNaturalPonytail(color: string): THREE.Group {
  const rand = seeded(53);
  const group = new THREE.Group();
  group.name = "ponytail-hair";
  const mat = toon(color, { roughness: 0.6 }, "hair");
  const line = hairline(0.15, 0.2);
  const tie = onSkull(Math.PI, 0.1, 0.024);
  const parts = [hairCapGeometry(line, 0.008)];
  // A light fringe off a side parting, shorter than long hair's.
  const FRINGE = 24;
  for (let k = 0; k < FRINGE; k++) {
    const t = 0.3 - (k / (FRINGE - 1)) * 0.8 + rand() * 0.02;
    const sweep = -0.3 - Math.abs(rand()) * 0.08;
    const end = 0.145 + Math.abs(rand()) * 0.01;
    parts.push(strandGeometry([onSkull(0.3, 0.2, 0.004), onSkull(t + sweep / 3, 0.185, 0.011), onSkull(t + sweep, end, 0.008)], STRAND));
  }
  // Pulled back: from the hairline round the sides and the nape, up over the skull to the tie.
  const BACK = 64;
  for (let k = 0; k < BACK; k++) {
    const theta = Math.PI * 0.42 + (k / (BACK - 1)) * Math.PI * 1.16 + rand() * 0.02;
    const root = line(theta) + 0.02;
    const mid = onSkull(Math.PI + (theta - Math.PI) * 0.5, (root + 0.1) / 2, 0.016);
    const end = tie.clone().add(new THREE.Vector3(rand() * 0.012, rand() * 0.01, 0.006));
    parts.push(strandGeometry([onSkull(theta, root, 0.006), mid, end], STRAND, 8));
  }
  group.add(hairMesh("hair", parts, mat, FRINGE + BACK, STRAND));
  // The tail: a core so it reads full, and strands over it, loosening toward the tip.
  const core = new THREE.Mesh(ponytailGeometry(0.85, 16, 14), mat);
  core.name = "ponytail";
  const curve = ponytailCurve();
  const TAIL = 56;
  const strands: THREE.BufferGeometry[] = [];
  const at = new THREE.Vector3();
  const frames = curve.computeFrenetFrames(8, false);
  for (let k = 0; k < TAIL; k++) {
    const a = (k / TAIL) * Math.PI * 2 + rand() * 0.1;
    const end = 0.92 + Math.abs(rand()) * 0.08;
    const pts = [0.02, 0.3, 0.6, end].map((t) => {
      curve.getPointAt(t, at);
      const i = Math.round(t * 8);
      const r = ponytailRadius(t) * (0.88 + Math.abs(rand()) * 0.08);
      return at
        .clone()
        .addScaledVector(frames.normals[i]!, Math.cos(a) * r)
        .addScaledVector(frames.binormals[i]!, Math.sin(a) * r * 0.85);
    });
    strands.push(strandGeometry(pts, STRAND * 1.2, 8));
  }
  const lay = hairMesh("ponytail-strands", strands, mat, TAIL, STRAND * 1.2);
  group.add(buildHairTie(tie.clone().add(new THREE.Vector3(0, 0, -0.008)), 0.03));
  group.add(buildTail(tie, [core, lay]));
  return group;
}
